import CloseIcon from '@mui/icons-material/Close';
import { Drawer, IconButton, Stack, Typography } from '@mui/material';
import PaiActionIconText from 'features/ui/base-components/pai-action-icon-text';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDatePicker from 'features/ui/base-components/pai-date-picker';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiMultiInput from 'features/ui/base-components/pai-multi-input';
import PaiRadioItem from 'features/ui/base-components/pai-radio-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Filter, FilterColumns, FilterLogics, FilterOperatorsText, FilterTypes, ManualTag, filterColumnTypes } from '../contact-management.types';

interface Props {
  open: boolean;

  filterLogic: FilterLogics;
  filters: Filter[];
  filtersLength: number;

  columns: OptionType[];

  manualTags: ManualTag[];
  campaignTags: string[];

  handleCloseFilterDrawer(): void;

  handleFilterLogicChange(value: string): () => void;

  removeFilter(idx: number): () => void;

  handleSelectFilterColumn(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSelectFilterOperator(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleFilterMultiInputFreeSoloChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>, newValue: string[]) => void;

  handleFilterMultiInputChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => void;

  handleFilterDateChange(idx: number): (newValue: Date) => void;

  handleFilterSelectChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => void;

  handleFilterValueChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  getOperators(type: FilterColumns): OptionType[];

  addFilter(): void;

  removeAllFilters(): void;

  handleApplyFilters(): void;
}

const ContactFilterDrawer: React.FC<Props> = ({
  open,
  filterLogic,
  filters,
  filtersLength,
  columns,
  manualTags,
  campaignTags,
  handleCloseFilterDrawer,
  handleFilterLogicChange,
  removeFilter,
  handleSelectFilterColumn,
  handleSelectFilterOperator,
  handleFilterMultiInputFreeSoloChange,
  handleFilterMultiInputChange,
  handleFilterDateChange,
  handleFilterSelectChange,
  handleFilterValueChange,
  getOperators,
  addFilter,
  removeAllFilters,
  handleApplyFilters,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const blockedCharacters = /[%&/?#]/;
    if (blockedCharacters.test(event.key)) {
      event.preventDefault();
    }
  };

  const manualTagOptions: OptionType[] = manualTags.map((tag) => ({ label: tag.name, value: tag.name }));
  const campaignTagOptions: OptionType[] = campaignTags.map((tag) => ({ label: tag, value: tag }));

  const allFiltersAreValid = useMemo(() => {
    return filters.every((filter) => {
      if (filter.operator === FilterOperatorsText.IS_EMPTY || filter.operator === FilterOperatorsText.IS_NOT_EMPTY) {
        return true;
      }
      if (filter.operator === FilterOperatorsText.IS_ANY_OF) {
        return !_.isEmpty(filter.values) || (typeof filter.values?.[0] === 'object' && (filter.values as OptionType[])?.every((f) => f.value !== ''));
      }
      if (
        filter.operator === FilterOperatorsText.EQUALS &&
        (filter.column === FilterColumns.MANUAL_TAGS || filter.column === FilterColumns.CAMPAIGN_TAGS)
      ) {
        return typeof filter.value === 'object' && (filter.value as OptionType).value !== '';
      }
      return !!filter.value;
    });
  }, [filters]);

  return (
    <Drawer classes={{ paper: classes.drawer }} anchor={'right'} open={open}>
      <Stack gap={4}>
        <Stack gap={2}>
          <div>
            <IconButton className={classes.closeButton} size={'small'} onClick={handleCloseFilterDrawer}>
              <CloseIcon />
            </IconButton>
          </div>
          <Typography className={classes.text}>{t('filterExplanation')}</Typography>
          {filters.length > 1 ? (
            <Stack>
              <PaiRadioItem
                value={FilterLogics.AND}
                selectedValue={filterLogic}
                label={t('allConditionsMustBeTrue')}
                handleSelectItem={handleFilterLogicChange}
              />
              <PaiRadioItem
                value={FilterLogics.OR}
                selectedValue={filterLogic}
                label={t('oneConditionMustBeTrue')}
                handleSelectItem={handleFilterLogicChange}
              />
            </Stack>
          ) : null}
          <Stack gap={1}>
            {filters.map((filter, index) => (
              <Stack key={`${filter.column}-${index}`} gap={2}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <PaiSelect value={filter.column} label={index === 0 ? t('filterColumn') : undefined} onChange={handleSelectFilterColumn(index)}>
                    {columns.map((option) => (
                      <PaiMenuItem key={option.value} value={option.value}>
                        {option.label}
                      </PaiMenuItem>
                    ))}
                  </PaiSelect>
                  <PaiSelect
                    value={filter.operator}
                    label={index === 0 ? t('filterOperator') : undefined}
                    onChange={handleSelectFilterOperator(index)}
                  >
                    {getOperators(filter.column).map((option) => (
                      <PaiMenuItem key={option.value} value={option.value}>
                        {option.label}
                      </PaiMenuItem>
                    ))}
                  </PaiSelect>
                  {filter.operator === FilterOperatorsText.IS_ANY_OF ? (
                    filter.column === FilterColumns.MANUAL_TAGS || filter.column === FilterColumns.CAMPAIGN_TAGS ? (
                      <PaiAutoComplete
                        options={filter.column === FilterColumns.MANUAL_TAGS ? manualTagOptions : campaignTagOptions}
                        selected={filter.values as OptionType[]}
                        multiple={true}
                        label={index === 0 ? t('filterValue') : undefined}
                        onChange={handleFilterMultiInputFreeSoloChange(index)}
                        disableClearable={true}
                      />
                    ) : (
                      <PaiMultiInput
                        value={filter.value as string}
                        values={filter.values as string[]}
                        label={index === 0 ? t('filterValue') : undefined}
                        placeholder={t('pressEnterToAdd')}
                        onChange={handleFilterMultiInputFreeSoloChange(index)}
                        onInputChange={handleFilterMultiInputChange(index)}
                      />
                    )
                  ) : filterColumnTypes[filter.column] === FilterTypes.DATE ? (
                    <PaiDatePicker
                      value={filter.value as Date}
                      label={index === 0 ? t('filterValue') : undefined}
                      views={['year', 'month', 'day']}
                      onChange={handleFilterDateChange(index)}
                    />
                  ) : filter.operator !== FilterOperatorsText.IS_EMPTY && filter.operator !== FilterOperatorsText.IS_NOT_EMPTY ? (
                    filter.operator === FilterOperatorsText.EQUALS &&
                      (filter.column === FilterColumns.MANUAL_TAGS || filter.column === FilterColumns.CAMPAIGN_TAGS) ? (
                      <PaiAutoComplete
                        options={filter.column === FilterColumns.MANUAL_TAGS ? manualTagOptions : campaignTagOptions}
                        selected={filter.value as OptionType}
                        label={index === 0 ? t('filterValue') : undefined}
                        onChange={handleFilterSelectChange(index)}
                        disableClearable={true}
                      />
                    ) : (
                      <PaiTextField
                        value={filter.value as string | number}
                        label={index === 0 ? t('filterValue') : undefined}
                        placeholder={t('enterFilterValueHere')}
                        type={filterColumnTypes[filter.column] === FilterTypes.NUMERIC ? 'number' : undefined}
                        onKeyDown={handleKeyDown}
                        onChange={handleFilterValueChange(index)}
                      />
                    )
                  ) : (
                    <div style={{ width: '100%' }} />
                  )}
                  {filters.length > 1 ? (
                    <PaiActionIconText variant={'delete'} mt={index === 0 ? 'calc(19.5px + 8px)' : undefined} action={removeFilter(index)} />
                  ) : null}
                </Stack>
              </Stack>
            ))}
          </Stack>
          <PaiActionIconText variant={'add'} text={t('addFilter')} action={addFilter} />
        </Stack>
        <Stack direction={'row'} justifyContent={'center'} gap={2}>
          <PaiButton text={t('applyFilters')} action={handleApplyFilters} disabled={!allFiltersAreValid} />
          {filters.length > 1 || filtersLength > 0 ? (
            <PaiActionIconText variant={'delete'} text={t('removeAllFilters', { count: filters.length })} action={removeAllFilters} />
          ) : null}
        </Stack>
      </Stack>
    </Drawer>
  );
};

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: '750px',
    padding: '16px',
  },
  closeButton: {
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
  },
}));

export default ContactFilterDrawer;
